import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Text,
  useColorModeValue,
  useToast,
  Wrap,
  WrapItem,
  Spinner,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import IconBox from "../../../../components/icons/IconBox";
import { HSeparator } from "../../../../components/separator/Separator.jsx";
import React, { useCallback, useEffect, useState } from "react";
import { MdCreditCard } from "react-icons/md";
import { restaurantService } from "../../../../services/restaurantService";

const OTHER_ID = "OTHER";

const MEAL_CARD_OPTIONS = [
  { id: "MULTINET", label: "Multinet" },
  { id: "PLUXEE", label: "Pluxee (Sodexo)" },
  { id: "TICKET_RESTAURANT", label: "Ticket Restaurant (Edenred)" },
  { id: "SETCARD", label: "Setcard" },
  { id: "METROPOL", label: "MetropolCard" },
  { id: "TOKEN_FLEX", label: "TokenFlex" },
  { id: "PAYE", label: "Paye" },
  { id: OTHER_ID, label: "Diğer" },
];

export default function MealCardSettings() {
  const toast = useToast();
  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const brandColor = useColorModeValue("brand.500", "white");
  const box = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const subtle = useColorModeValue("gray.600", "gray.400");

  const [selected, setSelected] = useState([]);
  const [otherNote, setOtherNote] = useState("");
  const [initialSelected, setInitialSelected] = useState([]);
  const [initialNote, setInitialNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await restaurantService.getRestaurantDetails();
      if (res.success && res.data) {
        const ids = Array.isArray(res.data.mealCardProviders)
          ? res.data.mealCardProviders
          : [];
        const note = res.data.mealCardOtherNote || "";
        setSelected(ids);
        setOtherNote(note);
        setInitialSelected(ids);
        setInitialNote(note);
      }
    } catch (e) {
      toast({
        title: "Yüklenemedi",
        description: "Yemek kartı bilgileri alınamadı.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) {
        const next = prev.filter((x) => x !== id);
        if (id === OTHER_ID) setOtherNote("");
        return next;
      }
      return [...prev, id];
    });
  };

  const dirty =
    JSON.stringify([...selected].sort()) !==
      JSON.stringify([...initialSelected].sort()) ||
    otherNote !== initialNote;

  const handleSave = async () => {
    try {
      setSaving(true);
      await restaurantService.updateRestaurantDetails({
        mealCardProviders: selected,
        mealCardOtherNote: selected.includes(OTHER_ID) ? otherNote : "",
      });
      setInitialSelected([...selected]);
      setInitialNote(selected.includes(OTHER_ID) ? otherNote : "");
      toast({
        title: "Kaydedildi",
        description: "Yemek kartı bilgileri güncellendi.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (e) {
      toast({
        title: "Kayıt başarısız",
        description:
          e?.response?.data?.message || "Bir hata oluştu, tekrar deneyin.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card mb={{ base: "0px", lg: "20px" }} w="100%">
      <Flex w="100%" align="center" gap="14px" mb="4px">
        <IconBox
          flexShrink={0}
          h="52px"
          w="52px"
          icon={
            <Icon as={MdCreditCard} color={brandColor} h="28px" w="28px" />
          }
          bg={box}
        />
        <Box flex="1" minW={0} textAlign="left">
          <Text color={textColorPrimary} fontWeight="bold" fontSize="xl" lineHeight="short">
            Yemek kartları
          </Text>
          <Text color={subtle} fontSize="sm" mt="4px">
            Müşteri uygulamasında işletme detayında gösterilir. Sadece geçerli olanları seçin.
          </Text>
        </Box>
      </Flex>
      <HSeparator mt="16px" />

      {loading ? (
        <Flex py="32px" justify="center" w="100%">
          <Spinner color={brandColor} />
        </Flex>
      ) : (
        <Box w="100%" mt="20px">
          <Text
            color={textColorPrimary}
            fontWeight="bold"
            mb="12px"
            fontSize="sm"
            alignSelf="start"
            w="100%"
            textAlign="left"
          >
            Kabul edilen kartlar
          </Text>
          <Wrap spacing="8px" justify="center">
            {MEAL_CARD_OPTIONS.map((opt) => (
              <WrapItem key={opt.id}>
                <Button
                  size="sm"
                  variant={selected.includes(opt.id) ? "solid" : "outline"}
                  colorScheme="brandScheme"
                  onClick={() => toggle(opt.id)}
                >
                  {opt.label}
                </Button>
              </WrapItem>
            ))}
          </Wrap>

          {selected.includes(OTHER_ID) && (
            <Box w="100%" mt="16px">
              <Text
                color={textColorPrimary}
                fontWeight="bold"
                mb="8px"
                fontSize="sm"
                textAlign="left"
              >
                Diğer (açıklama)
              </Text>
              <Input
                placeholder="Örn. kurum anlaşması, yerel kart adı…"
                value={otherNote}
                onChange={(e) => setOtherNote(e.target.value)}
                maxLength={200}
                size="sm"
              />
              <Text color={subtle} fontSize="xs" mt="4px" textAlign="right">
                {otherNote.length}/200
              </Text>
            </Box>
          )}

          <Button
            mt="20px"
            colorScheme="brand"
            size="sm"
            w="100%"
            onClick={handleSave}
            isLoading={saving}
            isDisabled={!dirty}
          >
            Kaydet
          </Button>
        </Box>
      )}
    </Card>
  );
}
